const WS_URL = 'wss://api.syphrax.com/ws';

import type { IncomingBlob } from './api';

type Listener = (payload: IncomingBlob) => void;
type ReconnectListener = () => void;

let socket: WebSocket | null = null;
const listeners = new Set<Listener>();
const reconnectListeners = new Set<ReconnectListener>();
let reconnectTimer: number | null = null;
let currentToken: string | null = null;
let hasConnectedOnce = false;
const desiredSubscriptions = new Set<string>(); // tags base64

function applySubscriptions() {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  for (const tag of desiredSubscriptions) {
    socket.send(JSON.stringify({ type: 'subscribe', receiver_tag: tag }));
  }
}

function connect() {
  if (!currentToken) return;
  socket = new WebSocket(`${WS_URL}?token=${encodeURIComponent(currentToken)}`);
  socket.onopen = () => {
    applySubscriptions();
    if (hasConnectedOnce) {
      for (const l of reconnectListeners) {
        try { l(); } catch (_e) { /* ignore */ }
      }
    }
    hasConnectedOnce = true;
  };
  socket.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data);
      if (msg.type === 'message' && msg.payload) {
        for (const l of listeners) l(msg.payload as IncomingBlob);
      }
    } catch {
      // ignore
    }
  };
  socket.onclose = () => {
    socket = null;
    if (currentToken) {
      reconnectTimer = window.setTimeout(connect, 3000);
    }
  };
  socket.onerror = () => {
    socket?.close();
  };
}

export const ws = {
  open(token: string): void {
    currentToken = token;
    connect();
  },
  close(): void {
    currentToken = null;
    desiredSubscriptions.clear();
    if (reconnectTimer) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    socket?.close();
    socket = null;
  },
  subscribe(tag: string): void {
    desiredSubscriptions.add(tag);
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'subscribe', receiver_tag: tag }));
    }
  },
  unsubscribe(tag: string): void {
    desiredSubscriptions.delete(tag);
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'unsubscribe', receiver_tag: tag }));
    }
  },
  unsubscribeAll(): void {
    if (socket && socket.readyState === WebSocket.OPEN) {
      for (const tag of desiredSubscriptions) {
        socket.send(JSON.stringify({ type: 'unsubscribe', receiver_tag: tag }));
      }
    }
    desiredSubscriptions.clear();
  },
  onMessage(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  onReconnect(listener: ReconnectListener): () => void {
    reconnectListeners.add(listener);
    return () => reconnectListeners.delete(listener);
  },
};
